/**
 * orderAccountKind - which account an order belongs to, as the admin reads it
 * (corporate Business PAD contract, 2026-10-25, I1).
 *
 * The API resolves the kind (orderAccount.resolveOrderAccountKind: the investor
 * account's account_kind first, else the order's investmentAccount, never the
 * user's entity_type) and sends it as `accountKind`, with `accountKindReason`
 * when it could not. That answer is the only one the admin reads, and any
 * reason in it leaves the order unresolved whatever `accountKind` says.
 *
 * There is no client-side fallback (review round 3, 2026-10-25). When the API
 * sent neither key, the order is unresolved (ACCOUNT_KIND_NOT_SENT, "the
 * server did not send the order's account kind"): the absence of the server's
 * resolution does not prove the order has no linked or conflicting investor
 * account, so the order's own investmentAccount is never read here (the
 * server's resolver already falls back to it). For an unresolved order no bank
 * block of either kind is shown, and nothing is confirmed or marked paid.
 * There is no fallback to the user's entity_type either, and none across
 * kinds.
 */

const KINDS = new Set(["ENTITY", "INDIVIDUAL"]);

/** -> { kind: 'ENTITY'|'INDIVIDUAL'|null, source: 'api'|null, reason: string|null } */
export function resolvedOrderKind(order) {
  const o = order && typeof order === "object" ? order : {};
  const answered =
    Object.prototype.hasOwnProperty.call(o, "accountKind") ||
    Object.prototype.hasOwnProperty.call(o, "accountKindReason");
  if (!answered) return { kind: null, source: null, reason: "ACCOUNT_KIND_NOT_SENT" };
  if (o.accountKindReason !== null && o.accountKindReason !== undefined) {
    return { kind: null, source: "api", reason: String(o.accountKindReason) || "ACCOUNT_KIND_UNRESOLVED" };
  }
  if (KINDS.has(o.accountKind)) return { kind: o.accountKind, source: "api", reason: null };
  return { kind: null, source: "api", reason: "ACCOUNT_KIND_UNRESOLVED" };
}

const KIND_LABELS = { ENTITY: "Company (ENTITY)", INDIVIDUAL: "Individual" };

export function accountKindLabel(kind) {
  return KIND_LABELS[kind] || "Not resolved";
}

const KIND_REASONS = {
  ACCOUNT_KIND_UNRESOLVED: "the order does not name an investment account",
  ACCOUNT_KIND_CONFLICT: "the order's investor account and its investment account disagree",
  INVESTOR_ACCOUNT_FOREIGN: "the order's investor account belongs to another user",
  ACCOUNT_KIND_NOT_SENT: "the server did not send the order's account kind",
};

/** Why an order has no kind, in words. */
export function accountKindReasonText(reason) {
  return KIND_REASONS[reason] || KIND_REASONS.ACCOUNT_KIND_UNRESOLVED;
}

const CATEGORY_LABELS = { BUSINESS: "Business PAD", PERSONAL: "Personal PAD" };

export function padCategoryLabel(category) {
  return CATEGORY_LABELS[category] || "Not resolved";
}

const VERIFICATION_LABELS = {
  UNVERIFIED: "Unverified",
  PENDING: "Pending review",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
};

export function verificationLabel(status) {
  return VERIFICATION_LABELS[status] || "Unverified";
}
